/**
 * SFTP Push tab of the Delivery Config page. Full CRUD editor for the NYCBS
 * SFTP-push config document (`api_config/pharmacy/nycbs_sftp_push/latest`):
 * column mapping, response-status mapping, the resubmit fetch filter, the
 * status-update rules, and general settings. Wrapped in the CMM value-search
 * context that the shared QueryBuilder needs.
 */
import { useMemo, useState } from "react";
import { Button } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../../../../components/loaderMessage/loaderMessage";
import {
  CmmInputUniqueValuesProvider,
  useCmmInputUniqueValuesContext,
} from "../../../../../context/cmmInputUniqueValuesContext";
import { buildDeliveryStepConditionConfig } from "../shared/deliveryStepConditionConfig";
import ColumnMappingTable from "./columnMappingTable";
import FetchFilterSection from "./fetchFilterSection";
import GeneralSettingsCard from "./generalSettingsCard";
import ResponseStatusMappingTable from "./responseStatusMappingTable";
import StatusUpdateConditionsSection from "./statusUpdateConditionsSection";
import { useNycbsSftpPushConfig } from "./useNycbsSftpPushConfig";

const NycbsSftpPushConfigInner = () => {
  const {
    form,
    loading,
    saving,
    error,
    isDirty,
    update,
    addColumn,
    addMappingRow,
    addStatusUpdate,
    handleDiscard,
    handleSave,
  } = useNycbsSftpPushConfig();

  const { cmmInputUniqueValues } = useCmmInputUniqueValuesContext();
  const conditionConfig = useMemo(
    () => buildDeliveryStepConditionConfig(cmmInputUniqueValues),
    [cmmInputUniqueValues],
  );

  const [resetKey, setResetKey] = useState(0);

  const onDiscard = () => {
    handleDiscard();
    setResetKey((k) => k + 1);
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-primaryGray-16 p-4">
        <div className="flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
          <LoaderMessage message="Loading SFTP push configuration..." />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-primaryGray-16 p-4">
      <div className="flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
        <div className="flex flex-shrink-0 flex-col border-b border-primaryGray-16 pb-4">
          <div className="text-h11 font-bold text-primaryGray-1">SFTP Push</div>
          <div className="mt-1 text-small text-primaryGray-4">
            Configure the NYCBS SFTP push file columns, response-status codes,
            and resubmit handling.
          </div>
        </div>

        {error ? (
          <p className="text-body1 text-red-600" role="alert">
            {error}
          </p>
        ) : (
          <div className="mb-1 mt-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-6">
            <GeneralSettingsCard
              filenamePrefix={form.filenamePrefix}
              validCmmStatuses={form.validCmmStatuses}
              onFilenamePrefixChange={(filenamePrefix) =>
                update({ filenamePrefix })
              }
              onValidCmmStatusesChange={(validCmmStatuses) =>
                update({ validCmmStatuses })
              }
            />
            <ColumnMappingTable
              resetKey={resetKey}
              rows={form.columns}
              onChange={(columns) => update({ columns })}
              onAdd={addColumn}
            />
            <ResponseStatusMappingTable
              conditionConfig={conditionConfig}
              cmmInputUniqueValues={cmmInputUniqueValues}
              rows={form.mappingRows}
              onChange={(mappingRows) => update({ mappingRows })}
              onAdd={addMappingRow}
            />
            <FetchFilterSection
              resetKey={resetKey}
              value={form.fetchFilter}
              onChange={(fetchFilter) => update({ fetchFilter })}
              config={conditionConfig}
            />
            <StatusUpdateConditionsSection
              resetKey={resetKey}
              groups={form.statusUpdates}
              onChange={(statusUpdates) => update({ statusUpdates })}
              onAdd={addStatusUpdate}
              config={conditionConfig}
            />
          </div>
        )}

        <div className="flex flex-shrink-0 flex-row items-center justify-end gap-2 border-t border-primaryGray-16 pt-2">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={onDiscard}
            disabled={!isDirty || saving}
            children="Cancel"
          />
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            disabled={!isDirty || saving}
            children={saving ? "Saving..." : "Save"}
          />
        </div>
      </div>
    </div>
  );
};

/** SFTP Push tab wrapped with the CMM value-search context the QueryBuilder needs. */
const NycbsSftpPushConfig = () => (
  <CmmInputUniqueValuesProvider>
    <NycbsSftpPushConfigInner />
  </CmmInputUniqueValuesProvider>
);

export default NycbsSftpPushConfig;
